/**
 * Лента на двери. Живёт в том же документе, что и витрина: фоновый iframe
 * на слабом ПК (Windows «лучшая производительность») просто замирает.
 *
 * Движение — только `translate3d` в requestAnimationFrame. Никакого
 * `filter`/`backdrop-filter` на движущемся слое: без GPU это стоп-кадр.
 */

import { useEffect, useRef } from 'react';

import rows from './lookTape.fragment.html?raw';

const PERIOD = 8000;

export function LookTape(): React.ReactElement {
  const reelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const reel = reelRef.current;
    if (!reel) return;
    if (reel.dataset.looped !== '1') {
      const kids = Array.from(reel.children);
      for (const node of kids) reel.appendChild(node.cloneNode(true));
      reel.dataset.looped = '1';
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let start = performance.now();
    let frame = 0;
    let running = true;

    const tick = (now: number): void => {
      if (!running) return;
      const t = ((now - start) % PERIOD) / PERIOD;
      reel.style.transform = `translate3d(0, ${-t * 50}%, 0)`;
      frame = requestAnimationFrame(tick);
    };

    const onVis = (): void => {
      if (document.hidden) {
        cancelAnimationFrame(frame);
        return;
      }
      start = performance.now();
      frame = requestAnimationFrame(tick);
    };

    document.addEventListener('visibilitychange', onVis);
    frame = requestAnimationFrame(tick);
    return () => {
      running = false;
      cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  return (
    <div className="nx-look-tape" aria-hidden="true">
      <div className="nx-look-reel" ref={reelRef} dangerouslySetInnerHTML={{ __html: rows }} />
    </div>
  );
}
